import uniq from 'lodash/uniq'
import { useMemo } from 'react'

import { vatRateRelatedLineColumns } from '@features/invoices/constants/vatRateRelatedLineColumns'
import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { InvoiceLineColumn } from '@features/invoices/types/invoiceLineColumn'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useWatch } from '../../../../../hooks'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { useInvoiceEditorForms } from '../contexts/invoiceEditorFormsContext'

// list of columns that need to always be displayed in the UI, regardless of what comes from the backend
const lineColumnsVisibilityOverrides: InvoiceLineColumn[] = [
  'description',
  'productType',
  'vatPct',
  'quantity',
  'unitPrice',
]

// line discounts aren't part of the user-configurable column visibility settings, so ensure
// they're still rendered on the document template whenever the ByItem discount mode is in use
const discountLineColumns: InvoiceLineColumn[] = ['discount', 'discountPct']

export const useInvoiceVisibleColumns = () => {
  const { invoiceLinesColumnsVisibilityForm } = useInvoiceEditorForms()
  const { organization } = useUserOrganization()
  const { discountMode } = useInvoiceEditorForm()
  const { control } = invoiceLinesColumnsVisibilityForm

  const selectedColumns = useWatch({ control, name: 'selectedColumns' })

  const visibleColumns = useMemo(() => {
    const mergedColumns = uniq([...selectedColumns, ...lineColumnsVisibilityOverrides])

    // selectedColumns can carry discount columns over from a previous document that used ByItem
    // mode, so strip them here whenever ByItem isn't active - otherwise a document template with no
    // per-line discount (e.g. a deposit invoice) would show an always-empty discount column.
    const columns =
      discountMode === InvoiceDiscountMode.ByItem
        ? uniq([...mergedColumns, ...discountLineColumns])
        : mergedColumns.filter((column) => !discountLineColumns.includes(column))

    // it's important that it's after handling lineColumnsVisibilityOverrides
    if (organization?.isVatExempted) {
      return columns.filter((column) => !vatRateRelatedLineColumns.includes(column))
    }

    return columns
  }, [discountMode, organization?.isVatExempted, selectedColumns])

  return { visibleColumns }
}
